<template lang="pug">
.category(ref="scrollToElement")
  err404(v-if="status === 'error'")
  .category-page(v-else)
    .main-container-wide
      .breadcrumbs-container(v-if="!$rogue.appContext")
        Breadcrumbs(v-if="isSearchResultsPage" :links="searchBreadcrumbLinks")
        Breadcrumbs(v-else :category-id="category.categoryId")
      h1.heading(v-if="categoryTopLinksText || !categoryTopLinksImage") {{ categoryHeading }}
      .renderBlockHeader(v-if="slots.header")
        RenderBlock(:block="slots.header" :product="{ sku: category.name }")
      Tabs(
        v-if="isSearchResultsPage"
        :modelValue="activeTab === 'index' ? 'The Index' : 'Products'"
        :tabs="['Products', 'The Index']"
        @update:modelValue="changeTab($event)"
      )
      empty-search-results(
        v-if="isSearchResultsPage && totalProducts === 0 && status === 'loaded' && activeTab === 'products'"
        data-cnstrc-search
        :data-cnstrc-search-term="cnstrcSearchTerm"
        :data-cnstrc-result-id="cnstrcResultId ?? undefined"
        data-cnstrc-num-results="0"
        :data-cnstrc-result-page="pageNumber"
        @tab="changeTab($event)"
      )
      .results(v-else-if="activeTab === 'products'" :class="categoryTopLinksImage ? 'with-image-links' : ''")
        h1.heading(v-if="categoryTopLinksImage") {{ categoryHeading }}
        category-image-links.image-links(
          v-if="categoryTopLinksImage"
          :categoryId="category.categoryId"
          :filters="filters"
          :activeFilters="activeFilters"
        )
        .product-count(v-show="totalProducts > 0 || isLoading")
          .product-count-skeleton(v-if="isLoading")
          span(v-else v-html="__('<strong>Showing {count}</strong> of {total} Products', { count: showingText, total: totalProducts })")
        .category-links
          .cat-links(v-if="categoryTopLinksText" v-html="topLinks.links" )
          .related-search-terms(
            v-if="isSearchResultsPage && relatedSearchTerms.length"
            data-cnstrc-related-searches
            :data-cnstrc-result-id="cnstrcResultId ?? undefined"
            :data-cnstrc-search-term="cnstrcSearchTerm"
          )
            span {{ __('Related search terms') }}: &nbsp;
            template(v-for='(term, i) in relatedSearchTerms' :key='term')
              rogue-link(:to='`/search?q=${encodeURIComponent(term)}`' :data-cnstrc-related-search-term='term') {{ term }}
              span(v-if='i < relatedSearchTerms.length - 1') ,&nbsp;
        .opts(:style="{ visibility: totalProducts > 0 ? 'visible' : 'hidden' }")
          RogueSelect.ctrl.per-page(aria-label="items per page" :options="perPageOpts" :modelValue="$route.query.n_item || perPage" @update:modelValue="setPerPage($event)")
          sort.ctrl.sort-by(aria-label="Sort by" :value="sortOrder" :sort-options="sortOptions")
        RogueButton.mobile-filter-btn(
          width="full"
          @click="facetsTransitionEnabled = true; mobileFacetsOpen = !mobileFacetsOpen"
        ) {{ __('Filter') }}
        .active-filters-title.font-weight-bold.font-size-s(v-if="activeFilters.length > 0") {{ __('FILTERED BY') }}
        filters-active.active-filters(:activeFilters="activeFilters" :filters="filters")
        .category-filters(v-if="!(category.topLinks || {}).type")
          template(v-if="isLoading && !((nextCategoryLevel || {}).value || []).length")
            .filter-chip-skeleton(v-for="n in 3" :key="n")
          template(v-else)
            rogue-link(
              v-for='filter in ((nextCategoryLevel || {}).value || [])'
              :to="filter.categoryRoute || filter.route"
              :key="filter.id"
              :id="filter.id"
            )
              .category-name {{ filter.text }}
              .category-count ({{ filter.count }})
        filters.facets#category-filters(
          :filters="filters"
          :order="filterOrder"
          :loading="isLoading"
          :sort-options="sortOptions"
          :class="{ open: mobileFacetsOpen, 'with-transition': facetsTransitionEnabled }"
          @closed="mobileFacetsOpen = false"
        )
        #category-results.products-wrapper(
          :data-cnstrc-search="isSearchResultsPage || undefined"
          :data-cnstrc-browse="!isSearchResultsPage || undefined"
          :data-cnstrc-search-term="cnstrcSearchTerm"
          :data-cnstrc-result-id="cnstrcResultId ?? undefined"
          :data-cnstrc-num-results="cnstrcNumResults"
          :data-cnstrc-result-page="cnstrcNumResults !== undefined ? pageNumber : undefined"
          :data-cnstrc-filter-name="!isSearchResultsPage ? 'group_id' : undefined"
          :data-cnstrc-filter-value="!isSearchResultsPage ? category.categoryId : undefined"
        )
          hover-card-placeholder(v-if="status === 'loading'" v-for='x in CARD_COUNT.medium' :key="x")
          template(v-else)
            content-slot.full-width(v-for="slot in bannerContent" :key="slot.id" :content="slot" full)
            template(v-for="item in gridItems" :key="item.key")
              content-slot(v-if="item.content" :content="item.content")
              hover-card(
                v-else
                :preload="item.index < 8"
                :product="item.product"
                :category="category"
                :position="item.index"
                :fetchPriority="(!categoryTopLinksImage && item.index === 0) ? 'high' : 'auto'"
                @click="trackClick(item.product, item.index)"
                v-element-visibility="(isVisible) => onHoverCardVisibility(isVisible, item.product, item.index)"
              )
            content-slot.full-width(v-for="slot in bottomContent" :key="slot.id" :content="slot" full)
          h2(v-show="products.length === 0 && status !== 'productsLoading'") {{ __('No Results') }}
        pagination.pagination(:pageNumber='pageNumber' :totalPages='totalPages')
        .facets-overlay(
          v-show="mobileFacetsOpen"
          @click="mobileFacetsOpen = false"
        )
      the-index-search-results(v-else-if="activeTab === 'index'" @indexPostsCount="setIndexPostsCount")
    .description(v-if='category && category.description && status !== "productsLoading" && showDescription')
      .bottom-content.main-container(v-html="category.description" v-translate="category.name")
    .renderBlockFooter(v-if="slots.footer")
      p.blank-column &nbsp;
      RenderBlock(:block="slots.footer" :product="{ sku: category.name }")
    product-compare-drawer(v-if='!isSearchResultsPage && category && hasCompareSelections' :categoryId='category.categoryId')
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onServerPrefetch, onBeforeUnmount, watch, defineAsyncComponent, useTemplateRef } from 'vue'
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'
import { useHead, type UseHeadInput } from '@unhead/vue'
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { useMQ } from 'sisu/js/mq'
import { vElementVisibility } from '@vueuse/components'
import Filters from './components/filters.vue'
import Sort from './components/sort.vue'
import FiltersActive from './components/filters-active.vue'
import pagination from './components/pagination.vue'
import emptySearchResults from './components/empty-search-results.vue'
import categoryImageLinks from './components/category-image-links.vue'
import hoverCard from './components/hover-card.vue'
import hoverCardPlaceholder from './components/hover-card-placeholder.vue'
import contentSlot from './components/content-slot.vue'
import theIndexSearchResultsSkeleton from './components/the-index-search-results-skeleton.vue'
import { getCategoryMetaTags } from './helpers/lib/category-meta-tags'
import { useRogue } from '@/js/services/Rogue'
import metadata from '@/js/helpers/metadata'
import { getAppliedValues } from '@/js/pages/category/helpers/index'
import { messages } from '@/js/app-bridge/messages'
import { type IReflektionFacet, type IReflektionPayloadOptions, type IReflektionProcessedProduct, type IReflektionProcessedResponse, widgets } from '@/js/services/rfk/types'
import type { CnstrcQueryOptions } from '@/js/services/RogueTypes'
import type { CnstrcContentSlot } from '@/js/services/cnstrc/types'
import { updateFacetRoutes } from '@/js/pages/category/helpers/lib/update-facet-routes'
import { buildGridItems } from '@/js/pages/category/helpers/lib/build-grid-items'
import { decodePbFilterValue, mergeSuggestedTerms } from '@/js/services/cnstrc/util'
import { appQueryParams } from '@/js/app-bridge/helpers'
import { deepEqual } from '@/js/services/Utils'
import { getTopLinksFromRB } from '@/js/stores/category-links'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import type { Category } from '@/js/services/RogueTypes'
import { isPromise } from '@/js/helpers/asyncHelpers'
import { CARD_COUNT } from '@/js/stores/productConstants'

const DEFAULT_ITEMS_PER_PAGE = 80

// How many related search terms the row under the results shows.
const RELATED_SEARCH_COUNT = 6

type Keyphrase = { text: string, freq: number, id: string }

type Suggestion = {
  category?: { url: string, text: string, freq: number, id: string },
  keyphrase?: Keyphrase | Keyphrase[],
  trending_category?: { url: string, text: string, id: string }
}

const categoryStore = useCategoryStore()
const reviewsStore = useReviewsStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()
const mq = useMQ()

// Hydrate persisted compare selections independent of product-compare-drawer's own
// mount — the drawer's async chunk is now gated on this state (see hasCompareSelections
// below), so it can no longer be relied on to trigger this itself.
onMounted(() => {
  categoryStore.initCompare()
})

const hasCompareSelections = computed(() => !!category.value && (categoryStore.compare[category.value.categoryId]?.length ?? 0) > 0)

const theIndexSearchResults = defineAsyncComponent({
  loader           : () => import('./components/the-index-search-results.vue'),
  loadingComponent : theIndexSearchResultsSkeleton,
  delay            : 0,
})
const productCompareDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "product-compare-drawer" */ 'Src/js/pages/compare/components/product-compare-drawer.vue'))

const isLoading = ref(true)
const loadError = ref(false)
const productsLoading = ref(false)
const products = ref<IReflektionProcessedProduct[]>([])
const contentSlots = ref<CnstrcContentSlot[]>([])
const filterOrder = ref<string[]>([])
const stableFilterOrder = ref<string[]>([])

const nextCategoryLevel = ref<{ value: IReflektionFacet['value'] }>({ value: [] })
const totalProducts = ref(-1)
const filters = ref<{ [key: string]: IReflektionFacet }>({})
const perPage = ref(DEFAULT_ITEMS_PER_PAGE)
const pageNumber = ref(1)
const totalPages = ref(1)
const suggestion = ref<Suggestion>({})
const mobileFacetsOpen = ref(false)
const facetsTransitionEnabled = ref(false)
// Search bots are finding big long tails by changing facets (and getting new A tags) before new ones are loaded
let lastATagHrefUpdate = Date.now()
const indexPostsCount = ref(-1)
const perPageOpts = [20, 40, 80].map(count => ({ label: `${count}`, value: count }))
let impressionEventTemplate: any = {}

const scrollToElementRef = useTemplateRef<HTMLElement>('scrollToElement')

const cnstrcResultId = ref<string | null>(null)
const cnstrcRawRequest = ref<Record<string, any> | null>(null)
const sortOptions = ref<{ label: string; value: string; selected: boolean }[] | undefined>(undefined)

useHead({
  script: computed(() => {
    if (!cnstrcResultId.value || !cnstrcRawRequest.value) return []

    return [{
      id        : 'cnstrc-data',
      type      : 'application/json',
      key       : 'cnstrc-data',
      innerHTML : JSON.stringify({ result_id: cnstrcResultId.value, request: cnstrcRawRequest.value }),
    }]
  }),
})

const useConstructor = !!rogue.featureFlags?.isOn('enable_constructor')

const cnstrcNumResults = computed<number | undefined>(() => {
  if (isLoading.value || productsLoading.value) return undefined

  return totalProducts.value >= 0 ? totalProducts.value : undefined
})

const cnstrcSearchTerm = computed(() =>
  isSearchResultsPage.value ? (route.query.q as string) || '' : undefined
)

// Constructor returns related searches on the search response, carried on suggestion.keyphrase.
// A term identical to the query is dropped — it links back to the page you are already on.
const relatedSearchTerms = computed(() => {
  const { keyphrase } = suggestion.value
  const terms = Array.isArray(keyphrase) ? keyphrase : keyphrase ? [keyphrase] : []
  const query = (cnstrcSearchTerm.value || '').trim().toLowerCase()
  const distinct = terms.filter(term => term?.text && term.text.trim().toLowerCase() !== query)

  return mergeSuggestedTerms(distinct, [], RELATED_SEARCH_COUNT).map(term => term.text)
})

const bannerContent = computed(() => contentSlots.value.filter(slot => slot.placement === 'banner'))

const bottomContent = computed(() => contentSlots.value.filter(slot => slot.placement === 'bottom'))

const gridItems = computed(() => buildGridItems(products.value, contentSlots.value))

const categories = computed(() => categoryStore.categories)

const id = computed<number>(() => route.meta.id as number)

const activeTab = computed(() => route.query.tab === 'theindex' ? 'index' : 'products')

const category = computed<Category>(() => categories.value[id.value] || {})

const topLinks = computed(() => {
  if (breadCrumbs.value && breadCrumbs.value.length) {
    return getTopLinksFromRB(breadCrumbs.value, rogue)
  }

  return category.value?.topLinks
})

const categoryTopLinksImage = computed(() => topLinks.value?.type === 'image')

const categoryTopLinksText = computed(() => topLinks.value?.type === 'text')

const widget = computed(() => isSearchResultsPage.value ? widgets.SEARCH_RESULTS : widgets.CATEGORY)

const status = computed(() => {
  if (loadError.value) {
    return 'error'
  }

  if (isLoading.value) {
    return 'loading'
  }

  if (productsLoading.value && Object.keys(filters.value).length === 0) {
    return 'loading'
  }

  if (productsLoading.value) {
    return 'productsLoading'
  }

  if (category.value.categoryId || isSearchResultsPage.value) {
    return 'loaded'
  }

  return 'error'
})

const showingText = computed(() => {
  const start = (pageNumber.value - 1) * perPage.value + 1
  let end = pageNumber.value * perPage.value

  if (end > totalProducts.value) {
    end = totalProducts.value
  }

  return `${start}-${end}`
})

const sortOrder = computed<string>(() => {
  const rawSort = route.query?.sort
  const querySort = Array.isArray(rawSort) ? rawSort[0] : (rawSort as string)

  if (querySort) {
    return querySort
  }

  if (`${category.value?.categoryId}` === '6141') {
    return 'newest'
  }

  return 'featured'
})

const categoryHeading = computed(() => {
  if (isSearchResultsPage.value) {
    if (activeTab.value === 'products' && totalProducts.value === 0) {
      return rogue.__('No results for: {searchTerm}', { searchTerm: route.query.q })
    }

    if (activeTab.value === 'index' && indexPostsCount.value === 0) {
      return rogue.__('No results for: {searchTerm}', { searchTerm: route.query.q })
    }

    return rogue.__('Search results for: {searchTerm}', { searchTerm: route.query.q })
  }

  const rbTitle = category.value?.roguebuilder?.props?.pageTitle

  if (rbTitle) {
    return rbTitle
  }

  const { pageTitle } = getCategoryMetaTags(route.query, category.value, rogue, totalPages.value)

  return pageTitle
})

const isSearchResultsPage = computed(() => (route.name as string || '').toLowerCase() === 'search')

const searchBreadcrumbLinks = computed(() => [{ name: rogue.__('Search results for: {query}', { query: route.query.q }) }])

const activeFilters = computed(() => {
  return Object.keys(route.query)
    .filter(key => filters.value[key] || /^cat\d+$/.test(key))
    .map(key => {
      const filterKey = key
      const facet = filters.value[key]
      const activeValues = getAppliedValues(key, route).map(val => (val === '5for5' ? 'black_friday_dropship' : val))

      if (!facet && /^cat\d+$/.test(key)) {
        // Constructor only ever applies numeric groupId values — an unrecognized non-numeric
        // catN (e.g. an RFK-style name) was never actually applied as a filter, so don't show
        // a "Filtered by" chip for it.
        return activeValues
          .filter(urlKey => !useConstructor || /^\d+$/.test(urlKey))
          .map(urlKey => {
            const query = { ...route.query, [key]: activeValues.filter(v => v !== urlKey) }

            if (activeValues.length === 1) delete query[key]

            return {
              key        : filterKey,
              text       : categoryStore.categories[Number(urlKey)]?.name ?? urlKey,
              clearRoute : { query },
            }
          })
      }

      return facet?.value.filter((val: any) => activeValues.indexOf(val.urlKey) !== -1).map((val: any) => {
        const query = {
          ...route.query,
          [key]: activeValues.filter(v => v !== val.urlKey),
        }

        if (activeValues.length === 1) {
          delete query[key]
        }

        return {
          key        : filterKey,
          text       : val.text,
          clearRoute : { query },
        }
      })
    })
    .flat()
})

const slots = computed(() => (category.value.roguebuilder?.slots || {}))

const breadCrumbs = computed(() => category.value.roguebuilder?.props?.breadcrumbs?.map((b: any) => ({ name: b.title, url: b.url, hide: b.hide })) || [])

const showDescription = computed(() => {
  if (category.value.roguebuilder && category.value.roguebuilder.props && Object.prototype.hasOwnProperty.call(category.value.roguebuilder.props, 'showDescription')) {
    return category.value.roguebuilder.props.showDescription
  }

  return true
})

function buildHeadInput(): UseHeadInput {
  if (isSearchResultsPage.value) {
    return metadata({
      title       : totalProducts.value > 0 ? rogue.__(categoryHeading.value) : rogue.__('Search Results'),
      description : '',
      image       : '',
      robots      : 'noindex,nofollow',
    }, rogue)
  }

  if (category.value.categoryId) {
    let description = category.value.meta?.description || category.value.description
    if (description) {
      description = rogue.__(description, category.value.name)?.replace(/<[^>]*>/gi, '')
    }

    const { title, canonical, next, prev, robots } = getCategoryMetaTags(route.query, category.value, rogue, totalPages.value)

    return metadata({ url: canonical, title, robots, description, next, prev }, rogue)
  }

  return {}
}

const headControl = useHead(buildHeadInput())

function updateHead() {
  headControl.patch(buildHeadInput())
}

watch([id, totalPages, totalProducts], updateHead)

watch(id, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    isLoading.value = true
    load()
  }
})

watch(() => route.path, () => {
  // If mobile filters are open before navigating to another url
  // we need to close the filters or the body keeps the `hide-overflow`
  // class preventing the scroll in the new url
  mobileFacetsOpen.value = false
})

watch(() => route.query, (newVal, oldVal) => {
  // If mobile filters are open, we don't want to run this
  // The customer should be able to select/deselect multiple
  // filters before the update runs in the `mobileFacetsOpen`
  // watcher

  // If we are just changing tabs, we dont need to update
  if (!deepEqual(
    Object.assign({}, newVal || {}, { tab: '' }),
    Object.assign({}, oldVal || {}, { tab: '' })
  )) {
    if (mobileFacetsOpen.value) {
      updateFilterLinks()
    } else if (isSearchResultsPage.value || category.value.categoryId) {
      runRfk()
    }
  }
}, {
  immediate : false,
  deep      : true,
})

watch(mq, (val) => {
  if (val.indexOf('desktop') !== -1) {
    mobileFacetsOpen.value = false
    // Disarm so the next mobile-width entry snaps instantly instead of animating —
    // only an explicit tap on the filter button should re-arm this.
    facetsTransitionEnabled.value = false
  }
})

watch(mobileFacetsOpen, (val, prev) => {
  // When the mobile filters drawer is closed trigger rfk
  if (val) {
    preventScroll()
  } else {
    allowScroll()
  }

  if (!val && prev) {
    runRfk()
  }
})

onServerPrefetch(() => {
  return load()
})

onBeforeUnmount(() => {
  allowScroll()
  headControl.dispose()
})

function trackView(product: IReflektionProcessedProduct, index: number) {
  if (rogue.ssr || !impressionEventTemplate) {
    return
  }

  const rfkProducts = [{ product, index }]

  rogue.track.listItemViewDebounce(Object.assign({}, impressionEventTemplate, { rfkProducts }))
}

function trackClick(product: IReflektionProcessedProduct, index: number) {
  rogue.track.track({
    type       : 'productClick',
    product,
    listType   : isSearchResultsPage.value ? 'search' : 'category',
    // import list entity
    // @ts-expect-error category and list entity are different types
    listEntity : isSearchResultsPage.value ? undefined : category.value,
    page       : pageNumber.value,
    position   : index + 1,
  })
}

function setPerPage(val: string) {
  const query = { ...route.query }
  query.n_item = val
  delete query.page_number
  router.push({ query })
}

function setIndexPostsCount(count: number) {
  indexPostsCount.value = count
}

function changeTab(tab: string) {
  const newQuery: RouteLocationRaw = { query: { q: route.query.q } }
  if (tab === 'The Index') {
    newQuery.query!.tab = 'theindex'
  }
  router.push(newQuery)
}

function reset() {
  totalProducts.value = -1
  filters.value = {}
  filterOrder.value = []
  stableFilterOrder.value = []
  sortOptions.value = undefined
  cnstrcResultId.value = null
  cnstrcRawRequest.value = null
  perPage.value = DEFAULT_ITEMS_PER_PAGE
  pageNumber.value = 1
  totalPages.value = 1
  products.value = []
  contentSlots.value = []
}

async function load() {
  reset()

  // Make sure our category exists
  if (!isSearchResultsPage.value) {
    if (!categories.value[id.value]) {
      isLoading.value = true
      await categoryStore.load(id.value, rogue)
    }
  }

  // If eOps has disabled a category, we should 404 and skip RFK.
  if (!isSearchResultsPage.value && (!category.value.isActive || !categories.value[id.value])) {
    loadError.value = true
    productsLoading.value = false
    if (rogue.ssr && rogue.ssrContext?.renderData) {
      rogue.ssrContext.renderData.status = 404
    }

    return
  }

  if (!isSearchResultsPage.value && rogue.i18n.enabled) {
    const loadTranslations = rogue.i18n.loadNamespace(category.value.name)
    if (loadTranslations) {
      await loadTranslations
    }
  }

  await runRfk()

  if (category.value && rogue.appContext && !rogue.ssr) {
    const data = {
      url   : `${rogue.localizedBaseURL}${route.fullPath}`,
      type  : 'home/category',
      title : category.value.meta?.title || category.value.name,
    }

    rogue.emitToApp(messages.PAGE_OPENED, data)
    rogue.emitToApp(messages.DOM_LOADED, {})
  }
}

function onHoverCardVisibility(isVisible: boolean, product: IReflektionProcessedProduct, index: number) {
  if (isVisible) {
    trackView(product, index)
  }
}

if (!rogue.ssr) {
  load()
}

async function runRfk(recursive = false) {
  loadError.value = false
  productsLoading.value = true

  // If we changed the filter links less than 1 second ago, this might be a search spider that is
  // repeatedly clicking links before the content has loaded.
  // We will call this again after the content has loaded anyway - which will remove any invalid combinations
  if ((Date.now() - lastATagHrefUpdate) > 1000) {
    updateFilterLinks()
  }

  const queryParams = parseRouterQuery() || {}

  // Redirect legacy RFK-style ?catN= URLs to the matching child category page.
  if (useConstructor && !isSearchResultsPage.value) {
    const CAT_REDIRECT_PARAMS = ['cat1', 'cat2', 'cat3', 'cat4', 'cat5'] as const
    for (const catKey of CAT_REDIRECT_PARAMS) {
      const catParam = route.query[catKey]
      const catValue = Array.isArray(catParam) ? catParam[0] : catParam
      if (catValue) {
        const child = category.value?.children?.find(c => c.name === catValue && c.isActive)
        if (child?.url) {
          router.replace(child.url)

          return
        }
      }
    }
  }

  if (!rogue.ssr) {
    const { type, order } = queryParams.sort?.[0] || {}

    impressionEventTemplate = {
      type        : 'listView',
      listType    : isSearchResultsPage.value ? 'search' : 'category',
      query       : route.query.q,
      rfkProducts : [],
      filters     : Object.keys(queryParams.filter || {}).map(key => ({ type: key, value: queryParams.filter[key] })),
      sorts       : [{ type, value: order }],
    }

    if (!isSearchResultsPage.value) {
      impressionEventTemplate.listEntity = {
        type : 'category',
        name : category.value.name,
        id   : category.value.categoryId,
        url  : category.value.url,
      }
    }
  }

  // Pre-load any catN categories from the query string so the "Filtered by" chips can display
  // their names. Fired in parallel with the API call below; awaited before filter synthesis.
  const catLoadPromises = useConstructor && isSearchResultsPage.value
    ? ['cat1', 'cat2', 'cat3', 'cat4']
      .map(k => route.query[k])
      .filter((id): id is string => typeof id === 'string' && !isNaN(Number(id)) && id !== '')
      .map(id => categoryStore.load(Number(id), rogue))
    : []

  // On search, the category facet is restricted to categories that appear in the shop
  // menu. Kick off the menu load in parallel with the search request; fail open (show
  // all) if it isn't ready when the facet is built.
  const menuPromise = useConstructor && isSearchResultsPage.value
    ? categoryStore.loadMenu(rogue).catch(() => {})
    : undefined

  let res: IReflektionProcessedResponse | undefined

  if (useConstructor) {
    const cnstrcOptions = parseConstructorOptions(queryParams)

    if (isSearchResultsPage.value) {
      const searchReq = rogue.cnstrc.getSearchResults(route.query.q as string, cnstrcOptions)
      res = searchReq?.then ? await searchReq : searchReq
    } else {
      const browseReq = rogue.cnstrc.getBrowseResults('group_id', String(category.value.categoryId), cnstrcOptions)
      res = browseReq?.then ? await browseReq : browseReq
    }

    // Build the category nav facet from Constructor's groups response.
    // For browse: the correct cat level depends on page depth (top-level → cat2, subcategory → cat3, etc.)
    //             and category.value.children supplies the drill-down URLs.
    // For search: groups are always top-level (cat1); no category children are available for URL matching.
    const cnstrcGroups = (res as any)?.cnstrc_groups
    const currentGroup = cnstrcGroups?.[0]

    if (currentGroup?.children?.length) {
      const catLevel = isSearchResultsPage.value
        ? `cat${(currentGroup.parents?.length ?? 0) + 1}`
        : `cat${(category.value.path?.length ?? 0) + 2}`

      // On search, only categories that appear in the shop menu may be listed as a
      // category facet. Fail open (show all) if the menu set hasn't loaded.
      let groupChildren = currentGroup.children
      if (isSearchResultsPage.value) {
        await menuPromise
        const menuIds = categoryStore.menuCategoryIds
        if (menuIds.size) {
          groupChildren = groupChildren.filter((child: any) => menuIds.has(Number(child.group_id)))
        }
      }

      res!.facet[catLevel] = {
        key                : catLevel,
        display_name       : 'Category',
        number_of_products : currentGroup.count,
        value              : groupChildren.map((child: any) => {
          const match = category.value?.children?.find((c: any) => c.name === child.display_name && c.isActive)

          return {
            text          : child.display_name,
            urlKey        : isSearchResultsPage.value ? child.group_id : child.display_name,
            count         : child.count,
            categoryRoute : match?.url,
          }
        }),
        total: groupChildren.length,
      }
    }

    cnstrcResultId.value = (res as any)?.result_id ?? null
    cnstrcRawRequest.value = (res as any)?.cnstrc_request ?? null

    if (res?.redirect_url) {
      const regex = new RegExp(`^.+${rogue.site.hostname.replace('www.', '')}`)
      rogue.track.track({
        type     : 'previewSearchSelection' as any,
        redirect : res.redirect_url.replace(regex, ''),
        query    : route.query.q as string,
      })
      router.replace(res.redirect_url.replace(regex, ''))

      return
    }
  } else {
    const options: IReflektionPayloadOptions = {
      widget      : widget.value,
      uri         : route.path,
      query       : isSearchResultsPage.value ? route.query.q as string : undefined,
      page_number : queryParams.page_number,
      n_item      : queryParams.n_item,
      sort        : queryParams.sort,
      filter      : queryParams.filter,
    }

    let rfkRes = rogue.rfk?.fetch(options, 1)

    if (isPromise(rfkRes)) {
      try {
        rfkRes = await rfkRes
      } catch (e: any) {
        if (e.message === 'category_not_found') {
          loadError.value = true
          if (rogue.ssr && rogue.ssrContext?.renderData) {
            rogue.ssrContext.renderData.status = 404
          }
        }
        productsLoading.value = false

        return
      }
    }

    if (rfkRes?.error === 'category_not_found') {
      loadError.value = true
      productsLoading.value = false

      return
    }

    if (rfkRes?.redirect_url) {
      const regex = new RegExp(`^.+${rogue.site.hostname.replace('www.', '')}`)
      if (!rogue.ssr) {
        rogue.track.track({
          type     : 'previewSearchSelection' as any,
          redirect : rfkRes.redirect_url.replace(regex, ''),
          query    : route.query.q as string,
        })
        router.replace(rfkRes.redirect_url.replace(regex, ''))

        return
      }
    }

    res = rfkRes as IReflektionProcessedResponse | undefined
  }

  // Dont show program delivery if it is only Not Applicable (RFK only — Constructor handles this via the dashboard)
  if (!useConstructor && res?.facet?.program_delivery?.value?.length === 1 && res.facet.program_delivery.value[0].text === 'Not Applicable') {
    delete res.facet.program_delivery
  }

  await Promise.all(catLoadPromises)

  filters.value = res?.facet || {}

  // When a Constructor search is filtered by group_id (catN in the URL), the API response may not
  // include the selected group in cnstrc_groups, so filters.value[catN] may be missing or may not
  // contain the selected urlKey. Synthesize the active facet value from the category store so the
  // "Filtered by" chip can render and be cleared.
  if (useConstructor && isSearchResultsPage.value) {
    for (const catKey of ['cat1', 'cat2', 'cat3', 'cat4'] as const) {
      const selectedId = route.query[catKey]
      if (!selectedId || typeof selectedId !== 'string') continue
      if (filters.value[catKey]?.value?.some((v: any) => v.urlKey === selectedId)) continue
      const cat = categoryStore.categories[Number(selectedId)]
      if (!cat) continue
      if (!filters.value[catKey]) {
        filters.value[catKey] = { key: catKey, display_name: 'Category', value: [], total: 0 } as any
      }
      filters.value[catKey].value.push({ id: selectedId, text: cat.name, urlKey: selectedId, count: 0, label: cat.name })
      filters.value[catKey].total = filters.value[catKey].value.length
    }
  }

  sortOptions.value = res?.sort_options?.map(o => ({
    label    : o.display_name,
    value    : `${o.sort_by}.${o.sort_order === 'ascending' ? 'asc' : 'desc'}`,
    selected : o.status === 'selected',
  }))

  if (!recursive && Object.keys(parseRouterQuery() || {}).length > Object.keys(queryParams || {}).length) {
    await runRfk(true)

    return
  }

  const updatedProducts = res?.content?.product?.value || []

  if (useConstructor) {
    reviewsStore.setSummariesFromProducts(updatedProducts)
  } else {
    reviewsStore.loadSummaries(updatedProducts.map(p => p.sku), rogue)
      .catch((err) => rogue.log.warn('Error in http call to bazaarvoice', err))
  }

  products.value = updatedProducts
  contentSlots.value = res?.cnstrc_content || []
  totalProducts.value = res?.total_item || 0

  if (isSearchResultsPage.value) {
    rogue.track.track({
      type             : 'search',
      searchTerm       : route.query.q as string,
      searchType       : 'full',
      numSearchResults : res?.n_item || 0,
    })
  }

  /**
   * If the filter has a corresponding category route associated with it then we will use that instead of appending
   * a filter parameter, unless the category is a special product category such as items that fall under 3 ships free
   *
   * cat2 and cat3 are category hierarchies within reflektion, and represent products in relation to the current category
   * selected
   */
  // RFK returns catN as facets that need categoryRoute enrichment and occasional text fixes.
  // Constructor builds catN from resp.groups with categoryRoute already set — skip these.
  if (!useConstructor) {
    if (filters.value.cat2) {
      filters.value.cat2.value.forEach((value: any) => {
        const child = category.value.children?.find(childCategory => childCategory.name === value.urlKey)
        if (child) {
          value.categoryRoute = child.url
        }
      })
    }

    if (filters.value.cat3) {
      filters.value.cat3.value.forEach((value: any) => {
        if (value.text === 'Concept 2 Rowers') {
          value.text = 'Rowers'
        }
        const child = category.value.children?.find(childCategory => childCategory.name === value.urlKey)
        if (child) {
          value.categoryRoute = child.url
        }
      })
    }

    if (filters.value.cat4) {
      filters.value.cat4.value.forEach((value: any) => {
        const child = category.value.children?.find(childCategory => childCategory.name === value.urlKey)
        if (child) {
          value.categoryRoute = child.url
        }
      })
    }
  }

  if (filters.value.is_salable) {
    filters.value.is_salable.value = filters.value.is_salable.value.filter(val => val.text !== 'Out of Stock')
    filters.value.is_salable.total = filters.value.is_salable.value.length
  }

  if (filters.value.shoesize) {
    filters.value.shoesize.value.sort((current: any, next: any) => current.text - next.text)
  }

  // Custom sort for Brands
  if (filters.value.manufacturer?.value) {
    const customBrandOrder = ['Rogue Fitness', 'OSO', 'Westside Barbell', 'Ghost', 'Reflect Fitness']
    filters.value.manufacturer.value.sort((a: any, b: any) => {
      const indexA = customBrandOrder.indexOf(a.text)
      const indexB = customBrandOrder.indexOf(b.text)

      // Check for cases where the brand is not in the custom order
      if (indexA === -1) return 1 // Move unknown brands to the end
      if (indexB === -1) return -1 // Move unknown brands to the end

      return indexA - indexB
    })
  }

  if (category.value?.name === 'Iron Game Programming' && filters.value.cat2?.value) {
    const customCat2Order = ['Rogue Fitness', 'CrossFit']

    filters.value.cat2.value.sort((a: any, b: any) => {
      const indexA = customCat2Order.indexOf(a.text)
      const indexB = customCat2Order.indexOf(b.text)

      // Check for cases where the cat is not in the custom order
      if (indexA === -1) return 1 // Move unknown cats to the end
      if (indexB === -1) return -1 // Move unknown cats to the end

      return indexA - indexB
    })
  }

  // Custom sort for the shirt size facet
  if (filters.value.shirtsize) {
    const customOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20']

    filters.value.shirtsize.value.sort((a: any, b: any) => {
      const indexA = customOrder.indexOf(a.text)
      const indexB = customOrder.indexOf(b.text)

      return indexA - indexB
    })
  }

  // Calculate the "Route" for each facet
  try {
    updateFilterLinks()
    updateSidebarFacets()
  } catch (e: any) {
    rogue.log.error('Error updating new facets', e)
  }

  pageNumber.value = res?.page_number || 1
  perPage.value = queryParams.n_item || perPage.value
  totalPages.value = res?.total_page || 1
  suggestion.value = res?.suggestion || {}

  productsLoading.value = false
  isLoading.value = false

  rogue.log.crumb('rfk', 'RFK results', { category: route.path, facets: route.query })

  // If the window is scrolled below the results grid, scroll back up to the top of the page.
  if (scrollToElementRef.value && !rogue.ssr) {
    const resultsEl = document.getElementById('category-results')
    if(resultsEl && resultsEl.getBoundingClientRect().top < 0) {
      setTimeout(() => scrollToElementRef.value!.scrollIntoView({ behavior: 'smooth' }))
    }
  }
}

/**
 * Update which facets appear in the sidebar and in what order.
 * Also sets the `nextCategoryFacet` filter, which points to the active category facet (cat1 / cat2 / cat3)
 */
function updateSidebarFacets() {
  const query = route.query
  const appliedFilters = filters.value

  let order
  // For Constructor: category.filters was built for RFK and doesn't match Constructor's facet names,
  // so bypass it and show everything Constructor returns. For RFK: use category.filters as the
  // whitelist so only configured facets appear.
  if (isSearchResultsPage.value || !category.value || !category.value.filters || useConstructor) {
    const allFacetKeys = Object.keys(appliedFilters || {})
      .filter(filter => !['cat1', 'cat2', 'cat3', 'cat4', 'cat5', 'category_names'].includes(filter))

    // Constructor reorders its facets array on filtered responses; lock in the first-response order.
    const newKeys = allFacetKeys.filter(k => !stableFilterOrder.value.includes(k))

    if (newKeys.length) {
      stableFilterOrder.value = [...stableFilterOrder.value, ...newKeys]
    }

    order = stableFilterOrder.value.filter(k => allFacetKeys.includes(k))
  } else {
    order = category.value.filters.filter(filterName => appliedFilters[filterName])
  }

  // Based on which category is currently active, we need to show the next level deep of category facet.
  // On Constructor browse pages a real subcategory selection redirects to a new category URL rather than
  // leaving catN in the query, so catN presence there can't be trusted to mean "already selected" — only
  // search results keep catN as real, persisted filter state. Ignore query catN for Constructor browse
  // and rely solely on category depth.
  const catQuery = (useConstructor && !isSearchResultsPage.value) ? {} : query
  const categoryDepth = isSearchResultsPage.value ? 0 : category.value.path?.length + 1
  if (!catQuery.cat4 && categoryDepth < 4) {
    if (catQuery.cat3 || categoryDepth >= 3) {
      if (appliedFilters.cat4) {
        order.unshift('cat4')
        nextCategoryLevel.value = appliedFilters.cat4
      }
    } else if (catQuery.cat2 || categoryDepth >= 2) {
      order.unshift('cat3')
      nextCategoryLevel.value = appliedFilters.cat3
    } else if (catQuery.cat1 || categoryDepth >= 1) {
      order.unshift('cat2')
      nextCategoryLevel.value = appliedFilters.cat2
    } else {
      order.unshift('cat1')
      nextCategoryLevel.value = appliedFilters.cat1
    }
  }

  if (category.value?.name === 'Iron Game Programming') {
    order.splice(order.indexOf('program_type'), 1)
    order.unshift('program_type')
  }

  // Make sure is_salable is at the top
  if (order.indexOf('is_salable') !== -1) {
    order.splice(order.indexOf('is_salable'), 1)
  }
  if (appliedFilters.is_salable) {
    order.unshift('is_salable')
  }

  // Add reviews facet
  if (appliedFilters.rating_summary && order.indexOf('rating_summary') === -1) {
    order.push('rating_summary')
  }

  // Make sure that any active facets that are not showing up are added so they can be deselected
  Object.keys(query || {})
    .filter(key => !key.match(/^cat[0-9]$/) && appliedFilters[key] && order.indexOf(key) === -1)
    .forEach(key => order.push(key))

  filterOrder.value = order
}

/**
 * Calculate the router link for each facet that will appear in the sidebar
 * Will be called whenever the query changes, or when we get a new set of facets.
 */
function updateFilterLinks() {
  lastATagHrefUpdate = Date.now()
  // We dont want to modify the original query
  updateFacetRoutes(filters.value, route.query as { [key: string]: string | string[] })
}

function parseRouterQuery() {
  const query: { [key: string]: any } = {}


  const { page_number, n_item, ...filter } = route.query

  if (page_number) {
    query.page_number = page_number as string
  }

  if (n_item && typeof n_item === 'string') {
    query.n_item = parseInt(n_item, 10)
  }


  if (sortOrder.value) {
    let [type, order] = sortOrder.value.split('.')

    if (!order) {
      const matchingOpt = useConstructor ? sortOptions.value?.find(o => o.value.startsWith(type + '.')) : undefined
      order = matchingOpt?.value.split('.')[1] ?? 'asc'
    }

    if (type === 'newest') {
      if (!useConstructor) {
        type = 'fmgnewdate'
      }
      order = 'desc'
    }

    if (type === 'featured') {
      order = 'desc'
    }

    query.sort = { value: [{ type, order }] }
  }

  // RFK uses category.filters as a whitelist; Constructor uses its own returned facet keys.
  let validFilters = (!useConstructor && category.value?.filters) || Object.keys(filters.value)

  if (!validFilters?.length && isSearchResultsPage.value && !useConstructor) {
    // RFK search results page has no category.filters whitelist — derive valid filters from URL params.
    // Constructor keeps an empty validFilters on first load so unknown params (bots, crawlers) are dropped.
    validFilters = Object.keys(filter || {})
      .filter(f => ['page_number', 'n_item', 'q', 'redirected_from', 'tab', 'locale', 'sort', 'nossr', 'FLUSH_SSR_REDIS'].indexOf(f) === -1)
      .filter(f => appQueryParams.indexOf(f) === -1)
      .filter(f => f.indexOf('utm_') !== 0 && f.indexOf('_') !== 0 && f.indexOf('trk_') !== 0)
  }
  validFilters = validFilters.concat(['cat1', 'cat2', 'cat3', 'cat4'])

  Object.keys(filter || {})
    .filter(key => validFilters.indexOf(key) !== -1)
    .forEach((key) => {
      const value = getAppliedValues(key, route).map(val => (val === '5for5' ? 'black_friday_dropship' : val))

      query.filter = query.filter || {}

      if (value && value.length > 0) {
        query.filter[key] = { value }
      }
    })

  return query
}

function parseConstructorOptions(queryParams: ReturnType<typeof parseRouterQuery>): CnstrcQueryOptions {
  const cnstrcOptions: CnstrcQueryOptions = {
    page       : queryParams.page_number ? parseInt(queryParams.page_number, 10) : undefined,
    numResults : queryParams.n_item || perPage.value,
  }

  const sortEntry = queryParams.sort?.value?.[0]
  if (sortEntry && sortEntry.type !== 'featured') {
    cnstrcOptions.sortBy = sortEntry.type
    cnstrcOptions.sortOrder = sortEntry.order === 'asc' ? 'ascending' : 'descending'
  }

  if (queryParams.filter) {
    // Some URL/RFK param names differ from Constructor facet names — translate before sending.
    const PARAM_MAP: Record<string, string> = {
      color_standard: 'color',
    }

    cnstrcOptions.filters = {}

    // Only pass the most-specific catN (highest N) as group_id — Constructor accepts one group at a time.
    const activeCatKey = Object.keys(queryParams.filter)
      .filter(k => /^cat\d+$/.test(k))
      .sort((a, b) => Number(b.slice(3)) - Number(a.slice(3)))[0]

    for (const [key, val] of Object.entries(queryParams.filter)) {
      if (/^cat\d+$/.test(key)) {
        // Selected catN value is the Constructor group_id — pass it as a group_id filter.
        // For browse: catN triggers a page redirect before fetch, so this is a no-op in practice.
        if (key === activeCatKey) {
          const values: string[] = ((val as any).value || []).filter((v: string) => /^\d+$/.test(v))
          if (values.length) {
            cnstrcOptions.filters!.group_id = values
          }
        }
        continue
      }
      const cnstrcKey = PARAM_MAP[key] ?? key
      let values: string[] = (val as any).value || []

      if (key === 'pb') {
        values = values.map(urlKey => filters.value?.pb?.value.find((v: any) => v.urlKey === urlKey)?.id ?? decodePbFilterValue(urlKey))
      }

      // rating_summary is a single-select range facet in Constructor: a star threshold N maps to
      // the range-string option value "N"-"inf" (i.e. "N stars & up"). The URL carries the clean N.
      cnstrcOptions.filters[cnstrcKey] = cnstrcKey === 'rating_summary'
        ? values.map(v => `"${v}"-"inf"`)
        : values
    }
  }

  return cnstrcOptions
}

</script>

<style lang="scss">
.cat-links {
  a {
    color: $color-black;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      color: $color-red-dark;
    }
  }

  .sep {
    padding: 0 $space-xs;

    &::before {
      content: "/";
    }
  }
}
</style>

<style lang="scss" scoped>
.category {
  min-height: 500px;
}

.results {
  display: grid;
  grid-auto-flow: row;
  align-items: start;
  grid-template-columns: 1fr 1fr minmax(100px, 128px);
  grid-template-areas:
    "heading              heading              heading"
    "product-count        product-count        sort"
    "links                links                links"
    "category-filters     category-filters     category-filters"
    "active-filters-title active-filters-title active-filters-title"
    "active-filters       active-filters       active-filters"
    "products             products             products"
    "pagination           pagination           pagination";

  @include all-desktop() {
    grid-template-columns: 200px 1fr 240px;
    grid-template-areas:
      "heading              heading              heading"
      "product-count        links                sort"
      "active-filters-title active-filters       active-filters"
      "facets               products             products"
      "facets               pagination           pagination";
    column-gap: 48px;

    .active-filters:deep(.filtered-by) {
      display: none;
    }
  }

  &.with-image-links {
    display: grid;
    grid-auto-flow: row;
    grid-template-rows: auto;
    grid-template-columns: 1fr 1fr minmax(100px, 128px);
    grid-template-areas:
      "heading              heading              heading"
      "image-links          image-links          image-links"
      "links                links                links"
      "category-filters     category-filters     category-filters"
      "active-filters       active-filters       active-filters"
      "product-count        product-count        sort"
      "products             products             products"
      "pagination           pagination           pagination";

    .active-filters-title {
      display: none;
    }

    .active-filters:deep(.filtered-by) {
      display: flex;
    }

    @include all-desktop() {
      grid-template-columns: 200px 1fr 240px;
      grid-template-rows: auto auto auto auto auto minmax(375px, 1fr) auto;
      grid-template-areas:
        "heading          image-links          image-links"
        "facets           image-links          image-links"
        "facets           active-filters       active-filters"
        "facets           links                links"
        "facets           product-count        sort"
        "facets           products             products"
        "facets           pagination           pagination";
      column-gap: 48px;

      .heading {
        grid-column: auto;
      }

      .active-filters-title {
        display: none;
      }

      .active-filter:deep(.filtered-by) {
        display: flex;
      }
    }

    @include not-desktop() {
      .heading {
        grid-column: auto / span 3;
      }

      .active-filters {
        margin-top: 16px;
      }

      .mobile-filter-btn {
        margin-bottom: 8px;
      }
    }
  }
}

.category-filters {
  grid-area: category-filters;
  white-space: nowrap;
  overflow-x: scroll;
  padding: $space-s 0 $space-s 0;
  font-size: $size-xs;
  -ms-overflow-style: none;
  clear: both; // no grid support fallback

  &::-webkit-scrollbar {
    display: none;
  }

  @include all-desktop() {
    display: none;
  }
}

.filter-chip-skeleton {
  height: $size-xs;
  width: 100px;
  margin-right: $size-s;
  border-radius: $radius-default;
  animation: skeleton-loading 1s linear infinite alternate;

  @include not-desktop() {
    display: inline-block;
  }

  @include all-desktop() {
    display: none;
  }

  &:nth-child(2) {
    width: 90px;
  }

  &:nth-child(3) {
    width: 90px;
  }
}

.image-links {
  grid-area: image-links;
}

.category-name {
  display: inline-block;
  margin-right: $space-xxxs;
  color: $color-black;
}

.category-count {
  display: inline-block;
  margin-right: $size-s;
  color: $color-gray-dark;
}

.category-page {
  margin-top: $space-m;
}

.facets {
  grid-area: facets;
  float: left; // no grid support fallback
  clear: left; // no grid support fallback
  align-self: normal;

  @include not-desktop() {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2;
    height: 100vh;
    width: 100%;
    max-width: 350px;
    transform: translateX(calc(-100% - 24px));

    &.open {
      transform: translateX(0);
    }

    &.with-transition {
      transition: transform 500ms ease-in-out;
    }
  }
}

.facets-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 0;
}

.product-count {
  grid-area: product-count;
  font-size: $size-xs;
  align-self: center;
  float: left; // no grid support fallback
}

@keyframes skeleton-loading {
  0% { background-color: $color-gray-x-light; }
  100% { background-color: $color-gray-light; }
}

.breadcrumbs-container {
  min-height: $size-xs;
  margin-bottom: $space-s;

  @media(min-width: $desktop) {
    margin-bottom: $space-m;
  }
}

.product-count-skeleton {
  height: $size-xs;
  width: 160px;
  border-radius: $radius-default;
  animation: skeleton-loading 1s linear infinite alternate;
}

.related-search-terms {
  font-size: $size-xs;
  align-self: center;
  padding: $space-xs 0;

  @include mobile() {
    line-height: 1.5;
  }
}

.pagination {
  grid-area: pagination;
  margin-bottom: $space-s;
  clear: both; // no grid support fallback
}

.products-wrapper {
  grid-area: products;
  display: flex;
  flex-wrap: wrap;
  float: right; // no grid support fallback
  min-width: 100%; // no grid support fallback

  @media screen and (min-width: 480px) {
    min-width: 75%; // no grid support fallback
  }

  &>div {
    flex-basis: 50%;

    @media screen and (min-width: 480px) {
      flex-basis: 33.333%;
    }

    @media screen and (min-width: 960px) {
      flex-basis: 25%;
    }
  }

  // Constructor content rules: banner/bottom slots span the whole results column, in-grid
  // slots occupy `grid_span` card columns (--slot-span, default 1) of the same flex track.
  &>.content-slot {
    &.full-width {
      flex-basis: 100%;
    }

    &:not(.full-width) {
      flex-basis: calc(50% * var(--slot-span, 1));

      @media screen and (min-width: 480px) {
        flex-basis: calc(33.333% * var(--slot-span, 1));
      }

      @media screen and (min-width: 960px) {
        flex-basis: calc(25% * var(--slot-span, 1));
      }
    }
  }
}

.opts {
  grid-area: sort;
  text-align: right;
  padding-bottom: $space-xs;
  display: none;
  float: right; // no grid support fallback
  margin-bottom: 0;

  .ctrl {
    display: inline-block;
    margin-bottom: 0;
    margin-right: $space-xxs;
  }

  .per-page {
    width: 62px;
  }

  .sort-by {
    width: 150px;
  }

  @include all-desktop() {
    display: flex;
    justify-content: flex-end;
    min-height: 54px;
  }
}

.category-links {
  grid-area: links;
  align-self: normal;
}

.cat-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  height: 100%;
  padding-left: $space-xxs;

  @include large-xl() {
    font-size: $size-m;
  }

  @include not-desktop() {
    font-size: $size-s;
    margin-top: $size-xxs;
    margin-bottom: $space-l;
    flex-wrap: nowrap;
    overflow-x: scroll;

    &::-webkit-scrollbar {
      width: 0;
      display: none;
      background: transparent;
    }
  }
}

.description {
  display: flex;
  justify-content: center;
  background-color: $color-gray-x-light;
  padding: $space-xxl $space-s;
  position: relative;
  clear: both; // no grid support fallback
}

.renderBlockFooter {
  display: flex;
  justify-content: center;
  background-color: $color-gray-x-light;
  padding: $space-xxl $space-s;
  text-align: justify;

  >div {
    width: 100%;
    max-width: $desktop;
    flex-grow: 1;
  }

  p.blank-column {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: 240px;

    @include not-desktop() {
      display: none;
    }
  }
}

.renderBlockHeader {
  padding: $space-m 0;
}

.bottom-content {
  max-width: $tablet;
  text-align: center;
}

.active-filters {
  grid-area: active-filters;
  margin-bottom: $space-s;
  clear: both; // no grid support fallback
}

.active-filters-title {
  grid-area: active-filters-title;
  margin-right: $space-xs;
  margin-top: $space-s;
  white-space: nowrap;

  @include not-desktop() {
    display: none;
  }
}

.button.mobile-filter-btn {
  min-width: 100px;
  grid-area: sort;

  @include all-desktop() {
    display: none;
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  .full-width {
    padding: 0 $app-gap-default;
  }

  .main-container {
    padding: $app-gap-default;
    margin: 0;
  }

  .renderBlockFooter {
    margin-bottom: 100px;
  }

  .product {
    margin-bottom: 0;
  }

  .button.mobile-filter-btn {
    margin: 0 !important; // needed
  }

  .facets {
    max-width: none;
    height: 100%;

    :deep() {
      .scroll {
        padding: $app-gap-default;
      }

      .checkbox.filter-check {
        margin-bottom: 0;
        padding-right: 0;
        padding-left: 0;
      }

      .apply-filters {
        padding-left: $app-gap-default;
        padding-right: $app-gap-default;
        min-height: 78px;

        .button {
          margin: 0 !important; // needed

          &:first-child {
            margin-right: $app-gap-default !important; // needed
          }
        }
      }
    }
  }
}
</style>
